import React, { useState } from 'react';
import { Badge } from './ui/badge';
import { Dialog, DialogContent } from './ui/dialog';
import { Package, Boxes, Weight, Hash, Tag } from 'lucide-react';
import Barcode from './Barcode';

const ProductCard = ({ product, variant = 'grid' }) => {
  const [open, setOpen] = useState(false);

  // ----------- LIST VARIANT -----------
  if (variant === 'list') {
    return (
      <>
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="group flex w-full items-center gap-4 rounded-xl border border-slate-200 bg-white p-3 text-left shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:border-emerald-300 hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"
        >
          <div className="flex h-16 w-20 shrink-0 items-center justify-center overflow-hidden rounded-md bg-slate-50 p-2">
            {product.image ? (
              <img src={product.image} alt={product.name} className="h-full w-full object-contain" loading="lazy" />
            ) : (
              <Package className="h-6 w-6 text-slate-300" />
            )}
          </div>
          <div className="min-w-0 flex-1">
            <p className="truncate text-xs font-medium uppercase tracking-wide text-emerald-700">{product.brand}</p>
            <h3 className="truncate text-sm font-semibold text-slate-900 group-hover:text-emerald-800">{product.name}</h3>
            <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500">
              <span className="font-mono">{product.code}</span>
              {product.weight && <span>· {product.weight}</span>}
              {product.unitsPerBox > 0 && <span>· {product.unitsPerBox} u/caja</span>}
            </div>
          </div>
          <Badge variant="secondary" className="hidden shrink-0 bg-emerald-50 text-emerald-800 hover:bg-emerald-50 sm:inline-flex">
            {product.category}
          </Badge>
        </button>
        <ProductDetailDialog open={open} setOpen={setOpen} product={product} />
      </>
    );
  }

  // ----------- COMPACT VARIANT -----------
  if (variant === 'compact') {
    return (
      <>
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="group flex w-full flex-col rounded-xl border border-slate-200 bg-white p-3 text-left shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:border-emerald-300 hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"
        >
          <div className="flex h-32 w-full items-center justify-center overflow-hidden rounded-md bg-slate-50 p-2">
            {product.image ? (
              <img src={product.image} alt={product.name} className="h-full w-full object-contain transition-transform duration-300 group-hover:scale-110" loading="lazy" />
            ) : (
              <Package className="h-10 w-10 text-slate-300" />
            )}
          </div>
          <p className="mt-2 truncate text-[10px] font-medium uppercase tracking-wide text-emerald-700">{product.brand}</p>
          <h3 className="line-clamp-2 text-xs font-semibold leading-snug text-slate-900">{product.name}</h3>
          <p className="mt-1 font-mono text-[10px] text-slate-500">{product.code}</p>
        </button>
        <ProductDetailDialog open={open} setOpen={setOpen} product={product} />
      </>
    );
  }

  // ----------- DEFAULT GRID VARIANT -----------
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="group flex w-full flex-col rounded-xl border border-slate-200 bg-white p-5 text-left shadow-sm transition-all duration-200 hover:-translate-y-1 hover:border-emerald-300 hover:shadow-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"
      >
        <div className="flex h-52 items-center justify-center overflow-hidden rounded-md bg-slate-50 p-3">
          {product.image ? (
            <img
              src={product.image}
              alt={product.name}
              className="max-h-full max-w-full object-contain transition-transform duration-300 group-hover:scale-110"
              loading="lazy"
            />
          ) : (
            <Package className="h-14 w-14 text-slate-300" />
          )}
        </div>
        <div className="mt-4 flex flex-col gap-1.5">
          <p className="text-xs font-medium uppercase tracking-wide text-emerald-700">{product.brand}</p>
          <h3 className="line-clamp-2 text-[15px] font-semibold leading-snug text-slate-900">{product.name}</h3>
          <Badge variant="secondary" className="mt-1 w-fit bg-emerald-50 text-emerald-800 hover:bg-emerald-50">
            {product.category}
          </Badge>
          <div className="mt-3 space-y-1 border-t border-slate-100 pt-3 text-xs text-slate-600">
            <p><span className="font-medium text-slate-700">Código:</span> {product.code}</p>
            <p><span className="font-medium text-slate-700">Peso:</span> {product.weight || '-'}</p>
            <p><span className="font-medium text-slate-700">Unidades/Caja:</span> {product.unitsPerBox || '-'}</p>
            {product.barcode && (
              <Barcode
                value={product.barcode}
                height={42}
                fontSize={11}
                className="mt-3 border border-slate-100 px-2 py-1.5"
              />
            )}
          </div>
        </div>
      </button>
      <ProductDetailDialog open={open} setOpen={setOpen} product={product} />
    </>
  );
};

// ----------- DETAIL MODAL (shared by all variants) -----------
const ProductDetailDialog = ({ open, setOpen, product }) => (
  <Dialog open={open} onOpenChange={setOpen}>
    <DialogContent className="max-h-[92vh] max-w-3xl overflow-y-auto p-0">
      <div className="grid gap-0 sm:grid-cols-2">
        <div className="relative flex min-h-[280px] items-center justify-center overflow-hidden bg-gradient-to-br from-slate-50 to-emerald-50 p-8">
          {product.image ? (
            <img src={product.image} alt={product.name} className="max-h-[60vh] max-w-full object-contain" />
          ) : (
            <Package className="h-24 w-24 text-slate-300" />
          )}
        </div>

        <div className="flex flex-col p-7">
          <p className="text-xs font-bold uppercase tracking-widest text-emerald-700">{product.brand}</p>
          <h2 className="mt-1 text-2xl font-bold leading-tight text-slate-900">{product.name}</h2>
          <Badge variant="secondary" className="mt-3 w-fit bg-emerald-50 text-emerald-800 hover:bg-emerald-50">
            {product.category}
          </Badge>

          <div className="mt-6 grid grid-cols-1 gap-3">
            <InfoRow icon={Hash} label="Código" value={product.code} mono />
            <InfoRow icon={Weight} label="Peso / Volumen" value={product.weight || 'No especificado'} />
            <InfoRow icon={Boxes} label="Unidades por caja" value={product.unitsPerBox || 'No especificado'} />
            {product.cosmeticAreaName && (
              <InfoRow icon={Tag} label="Área" value={product.cosmeticAreaName} />
            )}
            {product.cosmeticTypeName && (
              <InfoRow icon={Tag} label="Tipo" value={product.cosmeticTypeName} />
            )}
            {product.cosmeticLine && (
              <InfoRow icon={Tag} label="Línea" value={product.cosmeticLine} />
            )}
            {product.cosmeticShade && (
              <InfoRow icon={Tag} label="Tono" value={product.cosmeticShade} />
            )}
            {product.barcode && (
              <div className="rounded-lg border border-slate-100 bg-white px-4 py-3">
                <div className="mb-2 flex items-center gap-2">
                  <Tag className="h-4 w-4 text-emerald-700" />
                  <p className="text-xs uppercase tracking-wide text-slate-500">Código de barras</p>
                </div>
                <Barcode value={product.barcode} height={70} fontSize={14} />
              </div>
            )}
          </div>
        </div>
      </div>
    </DialogContent>
  </Dialog>
);

const InfoRow = ({ icon: Icon, label, value, mono = false }) => (
  <div className="flex items-center gap-3 rounded-lg border border-slate-100 bg-slate-50/60 px-4 py-3">
    <Icon className="h-4 w-4 text-emerald-700" />
    <div className="min-w-0 flex-1">
      <p className="text-xs uppercase tracking-wide text-slate-500">{label}</p>
      <p className={`text-sm font-medium text-slate-900 ${mono ? 'font-mono' : ''}`}>{value}</p>
    </div>
  </div>
);

export default ProductCard;
